import { useState } from 'react';
import AssetInput from '../../../../components/AssetInput';
import Button from '../../../../components/Button';
import PercentageSelector from '../../../../components/PercentageSelector';
import { OrderBookType, SIGNATURE_PENDING } from '../../../../config/constants';
import useErrorState from '../../../../context/error/useErrorState';
import useModalState from '../../../../context/modal/useModalState';
import { preventOverflow, toBigNumber } from '../../../../web3/utils';
import { UserPositionRaw } from '../../../../hooks/web3/trade/useUserPositions';
import { formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { config } from '../../../../config/config';
import useUserFee from '../../../../hooks/web3/useUserFee';
import { UserOrder } from '../../../../hooks/web3/trade/useUserOpenOrders';
import EstimatedPnl from './EstimatedPnl';
import PositionDetails from './PositionDetails';
import ExistingTPSL from './ExistingTPSL';
import useCreateOrder from '../../../../hooks/web3/trade/useCreateOrder';
import useToastState from '../../../../context/toast/useToastState';

type TPSLContentProps = {
    onClose: () => void;
};

const initialState = {
    takeProfit: '',
    stopLoss: '',
};

const TPSLContent: React.FC<TPSLContentProps> = ({ onClose }) => {
    const { setError } = useErrorState();
    const { modalData, openModal, closeModal, setModalData } = useModalState();
    const { setToast } = useToastState();

    const { userFee } = useUserFee();

    const createOrder = useCreateOrder();
    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;

    const [amount, setAmount] = useState<typeof initialState>(
        userPosition?.tpsl
            ? {
                  takeProfit: userPosition?.tpsl.takeProfit?.price ?? '',
                  stopLoss: userPosition?.tpsl.stopLoss?.price ?? '',
              }
            : initialState
    );

    const closeFee = userFee?.mul(parseEther(userPosition?.volume || '1')).div(10000);

    const takeProfitPnL = parseUnits(amount.takeProfit || '0', config.priceFeedDecimal)
        .sub(parseUnits(userPosition?.entryPrice || '0', config.priceFeedDecimal))
        .mul(userPosition?.isShort ? -1 : 1)
        .mul(parseUnits('1', config.priceFeedDecimal))
        .div(parseUnits(userPosition?.entryPrice || '1', config.priceFeedDecimal))
        .mul(parseUnits(userPosition?.volume || '1', config.priceFeedDecimal))
        .div(parseUnits('1', config.priceFeedDecimal))
        .sub(parseUnits(userPosition?.totalFeeAmount || '0', config.priceFeedDecimal));

    const stopLossPnL = parseUnits(amount.stopLoss || '0', config.priceFeedDecimal)
        .sub(parseUnits(userPosition?.entryPrice || '0', config.priceFeedDecimal))
        .mul(userPosition?.isShort ? -1 : 1)
        .mul(parseUnits('1', config.priceFeedDecimal))
        .div(parseUnits(userPosition?.entryPrice || '1', config.priceFeedDecimal))
        .mul(parseUnits(userPosition?.volume || '1', config.priceFeedDecimal))
        .div(parseUnits('1', config.priceFeedDecimal))
        .sub(parseUnits(userPosition?.totalFeeAmount || '0', config.priceFeedDecimal));

    const calculateTPSL = (
        percentage: number,
        userPosition: UserPositionRaw | UserOrder | undefined,
        decimals: number,
        type: 'take-profit' | 'stop-loss'
    ) => {
        return formatUnits(
            parseUnits(userPosition?.entryPrice || '0', decimals).add(
                parseUnits(userPosition?.entryPrice || '0', decimals)
                    .mul(percentage)
                    .div(100)
                    .mul(parseUnits('1', decimals))
                    .div(parseUnits(userPosition?.leverage || '1', decimals))
                    .mul(
                        userPosition?.isShort
                            ? type === 'take-profit'
                                ? -1
                                : 1
                            : type === 'take-profit'
                            ? 1
                            : -1
                    )
            ),
            decimals
        );
    };

    const handleTakeProfitPercentageClick = (percentage: number) => {
        if (userPosition?.tpsl.takeProfit) return;

        const takeProfitPrice = calculateTPSL(
            percentage,
            userPosition,
            config.priceFeedDecimal,
            'take-profit'
        );

        setAmount((prev) => ({
            ...prev,
            takeProfit: preventOverflow(
                takeProfitPrice,
                userPosition?.tradePair.decimalFormatting
            ),
        }));
    };

    const handleStopLossPercentageClick = (percentage: number) => {
        if (userPosition?.tpsl.stopLoss) return;

        const stopLossPrice = calculateTPSL(
            percentage,
            userPosition,
            config.priceFeedDecimal,
            'stop-loss'
        );

        setAmount((prev) => ({
            ...prev,
            stopLoss: preventOverflow(
                stopLossPrice,
                userPosition?.tradePair.decimalFormatting
            ),
        }));
    };

    const handleClose = () => {
        setAmount(initialState);
        onClose();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setAmount((prev) => ({
            ...prev,
            [name]: preventOverflow(value, config.priceFeedDecimal),
        }));
    };

    const validate = () => {
        if (userPosition?.isShort) {
            if (
                amount.takeProfit &&
                parseUnits(amount.takeProfit || '0', config.priceFeedDecimal).gt(
                    parseUnits(userPosition?.entryPrice || '0', config.priceFeedDecimal)
                )
            ) {
                return 'Take Profit Price must be lower than Entry Price!';
            }

            if (
                amount.stopLoss &&
                amount.takeProfit &&
                parseUnits(amount.stopLoss || '0', config.priceFeedDecimal).lt(
                    parseUnits(amount.takeProfit || '0', config.priceFeedDecimal)
                )
            ) {
                return 'Stop Loss Price must be higher than Take Profit price!';
            }
        } else {
            if (
                amount.takeProfit &&
                parseUnits(amount.takeProfit || '0', config.priceFeedDecimal).lt(
                    parseUnits(userPosition?.entryPrice || '0', config.priceFeedDecimal)
                )
            ) {
                return 'Take Profit Price must be higher than Entry Price!';
            }

            if (
                amount.stopLoss &&
                amount.takeProfit &&
                parseUnits(amount.stopLoss || '0', config.priceFeedDecimal).gt(
                    parseUnits(amount.takeProfit || '0', config.priceFeedDecimal)
                )
            ) {
                return 'Stop Loss Price must be lower than Take Profit Price!';
            }
        }

        return;
    };

    const confirm = async () => {
        if (!userPosition) throw new Error('UserPosition is undefined');

        try {
            openModal('waitingModal');
            setModalData((prev) => ({
                ...prev,
                status: SIGNATURE_PENDING,
            }));

            const errorMessage = validate();

            if (errorMessage) {
                setToast({ type: 'danger', message: errorMessage }, errorMessage);
                return;
            }

            if (amount.takeProfit && !userPosition.tpsl.takeProfit) {
                await placeOrder(
                    userPosition,
                    amount.takeProfit,
                    OrderBookType.TAKE_PROFIT
                );
            }

            if (amount.stopLoss && !userPosition.tpsl.stopLoss) {
                await placeOrder(userPosition, amount.stopLoss, OrderBookType.STOP_LOSS);
            }

            handleClose();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    const placeOrder = async (
        userPosition: UserPositionRaw | UserOrder,
        limitPrice: string,
        type: Extract<OrderBookType, OrderBookType.TAKE_PROFIT | OrderBookType.STOP_LOSS>
    ) => {
        await createOrder(
            OrderBookType.CLOSE,
            {
                positionId: userPosition.id,
                tradePair: userPosition.tradePair.address,
            },
            'ClosePositionOrder',
            userPosition.isShort,
            () => '0',
            {
                limitPrice,
                type,
                tradePair: userPosition.tradePair,
                userPosition,
            }
        );
    };

    return (
        <>
            <PositionDetails
                selectedTradePair={userPosition?.tradePair}
                userPosition={userPosition}
            />
            {userPosition?.tpsl.takeProfit ? (
                <ExistingTPSL
                    amount={userPosition.tpsl.takeProfit.price}
                    label='Take Profit'
                    orderId={userPosition.tpsl.takeProfit.orderId}
                    orderType={OrderBookType.CLOSE}
                />
            ) : (
                <AssetInput
                    label='Take Profit'
                    asset='USD'
                    name='takeProfit'
                    value={amount.takeProfit}
                    onChange={handleChange}
                    placeholder='-'
                />
            )}
            <PercentageSelector
                currentValue={
                    userPosition?.tpsl.takeProfit
                        ? userPosition?.tpsl.takeProfit.price
                        : amount.takeProfit
                }
                disableButtons={!!userPosition?.tpsl.takeProfit}
                onPercentageClick={handleTakeProfitPercentageClick}
                isActive={(percentage) => {
                    const price = userPosition?.tpsl.takeProfit
                        ? userPosition?.tpsl.takeProfit.price
                        : amount.takeProfit;

                    const calculatedPrice = calculateTPSL(
                        percentage,
                        userPosition,
                        config.priceFeedDecimal,
                        'take-profit'
                    );

                    // using 5 decimals here because we lose precision when sending data to server
                    return toBigNumber(
                        preventOverflow(price, 2),
                        config.priceFeedDecimal
                    ).eq(
                        toBigNumber(
                            preventOverflow(calculatedPrice, 2),
                            config.priceFeedDecimal
                        )
                    );
                }}
            />
            <EstimatedPnl
                trigger='Take Profit Market'
                amount={amount.takeProfit}
                pnl={formatUnits(
                    takeProfitPnL.sub(closeFee || '0'),
                    config.priceFeedDecimal
                )}
                pool={userPosition?.tradePair}
            />
            {userPosition?.tpsl.stopLoss ? (
                <ExistingTPSL
                    amount={userPosition.tpsl.stopLoss.price}
                    label='Stop Loss'
                    orderId={userPosition.tpsl.stopLoss.orderId}
                    orderType={OrderBookType.CLOSE}
                />
            ) : (
                <AssetInput
                    label='Stop Loss'
                    asset='USD'
                    name='stopLoss'
                    value={amount.stopLoss}
                    onChange={handleChange}
                    placeholder='-'
                />
            )}
            <PercentageSelector
                percentages={[5, 10, 25, 50]}
                currentValue={
                    userPosition?.tpsl.stopLoss
                        ? userPosition?.tpsl.stopLoss.price
                        : amount.stopLoss
                }
                disableButtons={!!userPosition?.tpsl.stopLoss}
                onPercentageClick={handleStopLossPercentageClick}
                isActive={(percentage) => {
                    const price = userPosition?.tpsl.stopLoss
                        ? userPosition?.tpsl.stopLoss.price
                        : amount.stopLoss;

                    const calculatedPrice = calculateTPSL(
                        percentage,
                        userPosition,
                        config.priceFeedDecimal,
                        'stop-loss'
                    );

                    // using 5 decimals here because we lose precision when sending data to server
                    return toBigNumber(
                        preventOverflow(price, 2),
                        config.priceFeedDecimal
                    ).eq(
                        toBigNumber(
                            preventOverflow(calculatedPrice, 2),
                            config.priceFeedDecimal
                        )
                    );
                }}
                decimals={config.priceFeedDecimal}
            />
            <EstimatedPnl
                trigger='Stop Market'
                amount={amount.stopLoss}
                pnl={formatUnits(
                    stopLossPnL.sub(closeFee || '0'),
                    config.priceFeedDecimal
                )}
                pool={userPosition?.tradePair}
            />
            <p className='remark'>
                This setting applies to the entire position. Take profit and stop loss
                automatically cancel after closing the position.
            </p>
            <Button
                onClick={confirm}
                disabled={
                    (!amount.takeProfit && !amount.stopLoss) ||
                    (!!userPosition?.tpsl.takeProfit && !!userPosition.tpsl.stopLoss)
                }
            >
                {(userPosition?.tpsl.takeProfit && userPosition?.tpsl.stopLoss) ||
                (!userPosition?.tpsl.takeProfit && !userPosition?.tpsl.stopLoss)
                    ? 'Confirm'
                    : userPosition?.tpsl.takeProfit
                    ? 'Set SL'
                    : 'Set TP'}
            </Button>
        </>
    );
};

export default TPSLContent;
